import { FlexColumnSpaced, FlexRowSmallSpaced, SpaceBetweenRow } from '@fox/layout/src/flex';
import { Padded } from '@fox/layout/src/style';
import React, { ReactElement } from 'react';
import { IconButton } from '../icon-button/index';
import { Icon, IconName } from '../icon/index';
import { LabeledCheckbox } from '../labeled-checkbox/index';
import { AccordionDetails, AccordionSummary, MuiAccordion } from '../mui-accordion/index';
import { Popover } from '../popover/index';
import { TextField } from '../text-field/index';
import { Typography } from '../typography/index';

const FIELD_INPUT_WIDTH = '25rem';

interface AccordionSectionProps {
	defaultExpanded?: boolean;
	onExpandedChange?: (expanded: boolean) => void;
	/** Forces the accordion to be expanded, overriding user interaction */
	forceExpanded?: boolean;
	summaryTitle: string;
	details: React.ReactNode;
	limitedWidth?: boolean;
	/**
	 * If this is set to true, the title of the accordion will become editable by the user.
	 */
	editing?: boolean;
	/**
	 * If true, disables the ability to edit the title. Other aspects are still editable if {@link AccordionSectionProps#editing} is true
	 */
	disableTitleEditing?: boolean;
	/**
	 * A handler for when the title is updated; used in conjunction with the 'editing' prop.
	 */
	onUpdateTitle?: (title: string) => void;
	/**
	 * Sets the label for the textfield in edit mode.
	 */
	fieldLabel?: string;
	/**
	 * A component that can be passed in for use in the accordion summary.
	 */
	summaryEditComponent?: ReactElement;
	/**
	 * Force the edit summary component to appear in readonly mode
	 */
	forceSummaryEditComponent?: boolean;

	summaryTitleIcon?: IconName | null | undefined;
	/**
	 * If true, removes padding from AccordionDetails.
	 */
	disableDetailsPadding?: boolean;
}

const SectionTitle = ({ summaryTitle, summaryTitleIcon }: Pick<AccordionSectionProps, 'summaryTitle' | 'summaryTitleIcon'>) => {
	return (
		<FlexRowSmallSpaced style={{ alignItems: 'center' }}>
			<Typography variant="h5">{summaryTitle}</Typography>
			{summaryTitleIcon ? <Icon iconName={summaryTitleIcon} /> : null}
		</FlexRowSmallSpaced>
	);
};

export const AccordionSection = ({
	defaultExpanded = false,
	onExpandedChange,
	forceExpanded,
	summaryTitle,
	details,
	limitedWidth,
	editing = false,
	disableTitleEditing = false,
	onUpdateTitle,
	fieldLabel,
	summaryEditComponent,
	forceSummaryEditComponent,
	summaryTitleIcon,
	disableDetailsPadding
}: AccordionSectionProps) => {
	// Use local state only when forceExpanded is not provided
	const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);

	// Derive the actual expanded state: forceExpanded takes precedence when defined
	const expanded = forceExpanded !== undefined ? forceExpanded : isExpanded;

	return (
		<MuiAccordion
			elevation={0}
			disableGutters={true}
			expanded={expanded}
			onChange={(_, newExpanded) => {
				// Only update local state when not force-controlled
				if (forceExpanded === undefined) {
					setIsExpanded(newExpanded);
				}
				onExpandedChange?.(newExpanded);
			}}
			square={true}
			sx={{
				border: 'none',
				'&:before': {
					display: 'none'
				}
			}}
			padLeft
		>
			<AccordionSummary disableRipple={true} expandIcon={<Icon iconName="KeyboardArrowDown" />}>
				{!editing ? (
					<SpaceBetweenRow>
						<SectionTitle summaryTitle={summaryTitle} summaryTitleIcon={summaryTitleIcon} />
						{forceSummaryEditComponent ? <div onClick={e => e.stopPropagation()}>{summaryEditComponent}</div> : null}
					</SpaceBetweenRow>
				) : (
					<SpaceBetweenRow onClick={e => e.stopPropagation()}>
						{disableTitleEditing ? (
							<SectionTitle summaryTitle={summaryTitle} summaryTitleIcon={summaryTitleIcon} />
						) : (
							<TextField
								style={{ flex: 1 }}
								defaultValue={summaryTitle}
								onChange={e => onUpdateTitle?.(e.target.value)}
								label={fieldLabel}
							/>
						)}
						{summaryEditComponent ?? <></>}
					</SpaceBetweenRow>
				)}
			</AccordionSummary>
			<AccordionDetails sx={disableDetailsPadding ? { padding: 0 } : undefined}>
				<FlexColumnSpaced style={limitedWidth ? { width: FIELD_INPUT_WIDTH } : {}}>{details}</FlexColumnSpaced>
			</AccordionDetails>
		</MuiAccordion>
	);
};

export const SectionSettingsPopover = ({
	expand,
	onExpand,
	children
}: {
	expand?: boolean;
	onExpand: (expand: boolean) => void;
	children?: React.ReactNode;
}) => {
	return (
		<Popover overlay={<SectionSettingsOverlay expand={expand ?? false} onExpand={onExpand} children={children} />}>
			<IconButton iconName="Settings" />
		</Popover>
	);
};

export const SectionSettingsOverlay = ({
	expand,
	onExpand,
	children
}: {
	expand: boolean;
	onExpand: (expand: boolean) => void;
	children?: React.ReactNode;
}) => {
	return (
		<Padded>
			<FlexColumnSpaced>
				<Typography variant="h5" component="h1">
					Update Section
				</Typography>
				<LabeledCheckbox
					label="Expand section by default"
					onChange={(_, checked) => onExpand(checked)}
					checkboxProps={{ defaultChecked: expand }}
				/>
				{children}
			</FlexColumnSpaced>
		</Padded>
	);
};
